import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { NotificationBell } from '@dfq/shared/components/NotificationBell';
import type { Notification } from '@dfq/shared/api/notifications';
import { useAppDispatch, useAppSelector } from '@dfq/shared/stores/hooks';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllRead,
  markNotificationRead,
  selectNotifications,
  selectNotificationsLoading,
  selectUnreadCount,
} from '@dfq/shared/stores/notifications';

/**
 * SH-NOTIFY-002 — Console-side container for the shared
 * `<NotificationBell>` capsule.
 *
 * Per `.claude/rules/frontend-data-flow.md` the capsule is presentational;
 * this container owns:
 *
 *  - 60-second polling of the unread-count endpoint (clears on unmount
 *    AND when `auth.isAuthenticated` flips false — i.e. logout).
 *  - `fetchNotifications` dispatch on popover open (transition-only,
 *    not on every poll cycle).
 *  - Optimistic mark-read + mark-all-read (the slice already implements
 *    optimistic updates internally; the container only dispatches).
 *  - Navigation to the notification's `action_url` via React Router.
 *  - Suppressing the "View all" footer link in the admin context — there
 *    is no `/admin/notifications` route today, so we pass
 *    `onViewAll={undefined}` and the capsule conditionally hides the
 *    link entirely.
 */

const POLL_INTERVAL_MS = 60_000;

export function NotificationBellContainer() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const notifications = useAppSelector(selectNotifications);
  const unreadCount = useAppSelector(selectUnreadCount);
  const isLoading = useAppSelector(selectNotificationsLoading);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [open, setOpen] = React.useState(false);

  // 60s polling of the unread-count endpoint while authenticated. Cleared
  // on unmount and on the auth flip so a logged-out tab doesn't keep
  // hitting the API. Errors are swallowed inside the thunk's rejection
  // handler — the slice records `state.error` but the capsule does NOT
  // surface it, per AC ("API errors are silently swallowed").
  React.useEffect(() => {
    if (!isAuthenticated) return;

    void dispatch(fetchUnreadCount());
    const intervalId = window.setInterval(() => {
      void dispatch(fetchUnreadCount());
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch, isAuthenticated]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        // Refresh the full list when the user opens the panel — the
        // background poll only refreshes the badge count.
        void dispatch(fetchNotifications());
      }
    },
    [dispatch],
  );

  const handleItemClick = React.useCallback(
    (notification: Notification) => {
      if (notification.read_at === null) {
        void dispatch(markNotificationRead(notification.id));
      }
      setOpen(false);
      if (notification.action_url !== null && notification.action_url !== '') {
        navigate(notification.action_url);
      }
    },
    [dispatch, navigate],
  );

  const handleMarkAllRead = React.useCallback(() => {
    void dispatch(markAllRead());
  }, [dispatch]);

  // Admin context: there is no /admin/notifications route today (per the
  // CON-AUTH-001 router definitions). Pass undefined so the capsule
  // suppresses the "View all" link entirely. If/when the route lands a
  // future ticket can wire `onViewAll: () => { setOpen(false); navigate('/admin/notifications'); }`.
  return (
    <NotificationBell
      notifications={notifications}
      unreadCount={unreadCount}
      isLoading={isLoading}
      open={open}
      onOpenChange={handleOpenChange}
      onItemClick={handleItemClick}
      onMarkAllRead={handleMarkAllRead}
      onViewAll={undefined}
    />
  );
}
